'use client'

import { ReactNode, useState } from 'react'
import { IconPencil } from '@tabler/icons-react'

import {
  SRFormData,
  SyneriseFormEvent,
  syneriseSendFormData
} from 'lib/synerise'
import Button from 'shared/ui/Button'
import { Modal, ModalTitle } from 'shared/ui/Modal'
import toast from 'shared/ui/utils/toast'
import { getApiErrors } from 'shared/utils/errors'

import { UpdateCustomerValuesPayload } from '../api'
import { useCustomerData } from '../hooks/useCustomerData'

import styles from './ProfileDetailsSingleItem.module.scss'

interface ProfileDetailsSingleItemProps {
  id?: string
  children: ReactNode
  icon: React.ReactNode
  label: string
  value: string
  buttonText?: string
  modalTitle?: string
  values: UpdateCustomerValuesPayload
  isSubmitDisabled?: boolean
  isButtonDisabled?: boolean
  openCallback: () => void
}

const ProfileDetailsSingleItem = ({
  children,
  icon,
  label,
  value,
  buttonText = 'Edytuj',
  modalTitle,
  values,
  isSubmitDisabled,
  isButtonDisabled,
  openCallback,
  id
}: ProfileDetailsSingleItemProps) => {
  const [open, setOpen] = useState(false)
  const { update } = useCustomerData()
  const [loading, setLoading] = useState(false)

  const handleOpen = () => {
    setOpen(true)
    openCallback()
  }
  const handleClose = () => setOpen(false)

  const handleSubmit = async () => {
    setLoading(true)
    await update(values)
      .then(async () => {
        setLoading(false)
        toast.success('Pomyślnie zapisano zmiany')

        // `phoneNumber` is deliberately not sent (KAN-3485): Synerise matches on an
        // internationally prefixed number and the API returns it without the prefix.
        const {
          isNewsletterSubscriber,
          dateOfBirth,

          phoneNumber,

          currentPassword,

          newPassword,

          newPasswordConfirmation,
          ...syneriseRestData
        } = values

        const formData: SRFormData = syneriseRestData

        if (isNewsletterSubscriber !== undefined) {
          formData.newsletterSubscriber = isNewsletterSubscriber
        }

        if (dateOfBirth !== undefined) {
          formData.birthdate = dateOfBirth
        }

        syneriseSendFormData({
          formData,
          event: SyneriseFormEvent.PROFILE_EDIT,
          fieldMapping: {
            birthdate: 'birthdate',
            firstname: 'firstName',
            lastname: 'lastName'
          }
        })
        handleClose()
      })
      .catch(err => {
        setLoading(false)
        console.error(err)
        toast.error(getApiErrors(err))
      })
  }

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (isSubmitDisabled || loading) return

    void handleSubmit()
  }

  return (
    <>
      <div className={styles.wrapper} id={id}>
        <div className={styles.content}>
          <div className={styles.iconWrapper}>{icon}</div>
          <div className={styles.details}>
            <div className={styles.detailsLabel}>{label}</div>
            <div className={styles.detailsValue}>{value}</div>
          </div>
          <Button
            id='profile_edit_button'
            startIcon={<IconPencil />}
            variant='neutralOutline'
            onClick={handleOpen}
            disabled={isButtonDisabled}
            aria-label={modalTitle}
          >
            {buttonText}
          </Button>
          <Modal id={`${id}-modal`} open={open} onClose={handleClose}>
            <ModalTitle>{modalTitle}</ModalTitle>
            <form noValidate onSubmit={handleFormSubmit}>
              {children}
              <div className={styles.buttonsWrapper}>
                <Button
                  id='DoB_save_button'
                  type='submit'
                  fullWidth
                  variant='primary'
                  disabled={isSubmitDisabled || loading}
                >
                  Zapisz
                </Button>
                <Button
                  id='DoB_cancel_button'
                  type='button'
                  fullWidth
                  onClick={handleClose}
                  variant='neutralOutline'
                >
                  Anuluj
                </Button>
              </div>
            </form>
          </Modal>
        </div>
      </div>
    </>
  )
}

export default ProfileDetailsSingleItem
